import { ApiRequests, ApiRequestsTypes } from '@tableau/data-catalog-client-components';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Formatters, Messages } from '../../codegen/Localize';
import { assertUnreachable } from '../helpers/TypescriptHelpers';
import { Module } from '../Module';
import { SiteRolesResource } from '../../userManagement/services/resources/SiteRoles';
import { Tables } from '../resources/Tables';
import { GetAllowedClassesRequest, IGetAllowedClassesParams, IGetAllowedClassesResult } from '../ServerApi/requests/GetAllowedClasses';
import { GetDatabaseActionsRequest } from '../ServerApi/requests/GetDatabaseActionsRequest';
import { GetDatasourceActionsRequest } from '../ServerApi/requests/GetDatasourceActionsRequest';
import { GetFlowActionsRequest } from '../ServerApi/requests/GetFlowActionsRequest';
import { GetTableActionsRequest } from '../ServerApi/requests/GetTableActionsRequest';
import { GetWorkbookActionsRequest } from '../ServerApi/requests/GetWorkbookActionsRequest';
import { Request } from '../ServerApi/requests/Request';
import { ISendImpactAnalysisEmailsParams, SendImpactAnalysisEmailsRequest } from '../ServerApi/requests/SendImpactAnalysisEmailsRequest';
import { SetColumnDescriptionRequest } from '../ServerApi/requests/SetColumnDescriptionRequest';
import { ISingleIdParams } from '../ServerApi/types/actionParams';
import { IEmptyResult } from '../ServerApi/types/IResponse';
import { TableAllowedActions } from '../ServerApi/types/TableAllowedActions';
import { RestApiErrorResponse } from './RestApiService';
import { ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';
import { SearchSuggestionsService } from './SearchSuggestionsService';

type CatalogActionsRequestArgs = {
  contentType: 'database' | 'table' | 'flow' | 'workbook' | 'datasource';
  contentId: string;
};

interface CatalogPermissionsResponse {
  sendEmail: boolean;
}

function getContentActionsRequest(args: CatalogActionsRequestArgs): Request<ISingleIdParams, CatalogPermissionsResponse> {
  switch (args.contentType) {
    case 'database': return new GetDatabaseActionsRequest(args.contentId);
    case 'table': return new GetTableActionsRequest(args.contentId);
    case 'flow': return new GetFlowActionsRequest(args.contentId);
    case 'workbook': return new GetWorkbookActionsRequest(args.contentId);
    case 'datasource': return new GetDatasourceActionsRequest(args.contentId);
    default: assertUnreachable(args.contentType);
  }
}

export class DataCatalogApiRequestsService implements ApiRequests {
  private toaster = ToasterService.instance;

  public static $inject = [
    'ServerService',
    'SearchSuggestionsService',
  ];

  constructor(
    private server: ServerService,
    private suggestions: SearchSuggestionsService
  ) { }

  public readonly searchMatchingTags = async (args: ApiRequestsTypes.SearchMatchingTagsArgs): Promise<ReadonlyArray<string>> => {
    const response = await this.suggestions.tags(args.partialText).getSlice(args.startIndex, args.maxItems);
    return response.items.map(t => t.name);
  }

  public readonly sendImpactAnalysisEmailAndShowToast = async (args: ISendImpactAnalysisEmailsParams): Promise<void> => {
    return this.server.sendRequest(new SendImpactAnalysisEmailsRequest(args))
      .then((result: any) => {
        if (result && !result.errors) {
          this.toaster.pop({ type: ToastType.Info, text: Messages.dataCatalogEmailSuccess() });
        }

        if (!result || result.errors) {
          this.toaster.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
        }
      });
  }

  public readonly setColumnDescription = async (args: ApiRequestsTypes.SetColumnDescriptionArgs): Promise<void> =>  {
    return this.server.sendRequest(new SetColumnDescriptionRequest(args.columnVizportalId, args.description))
      .then((result: IEmptyResult) => {
        if (result && !result.errors) {
          this.toaster.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
          return Promise.resolve();
        } else {
          this.toaster.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
          return Promise.reject();
        }
      });
  }

  public readonly canUserSetColumnTags = async (args: ApiRequestsTypes.CanUserSetColumnTagsArgs): Promise<boolean> => {
    return this.server.sendRequest(new GetTableActionsRequest(args.tableVizportalId))
      .then((result: TableAllowedActions) => {
        if (result) {
          return Promise.resolve(result.setDescription);
        } else {
          return Promise.reject();
        }
      });
  }

  public readonly canUserSetColumnDescription = async (args: ApiRequestsTypes.CanUserSetColumnDescriptionArgs): Promise<boolean> => {
    return this.server.sendRequest(new GetTableActionsRequest(args.tableVizportalId))
      .then((result: TableAllowedActions) => {
        if (result) {
          return Promise.resolve(result.setDescription);
        } else {
          return Promise.reject();
        }
      });
  }

  public readonly canUserSendImpactAnalysisEmail = async (args: CatalogActionsRequestArgs): Promise<boolean> => {
    return this.server.sendRequest(getContentActionsRequest(args)).then(r => {
      if (r) {
        return Promise.resolve(r.sendEmail);
      } else {
        return Promise.reject();
      }
    });
  }

  public readonly getAllowedClasses = async (args: IGetAllowedClassesParams): Promise<IGetAllowedClassesResult> => {
    return this.server.sendRequest(new GetAllowedClassesRequest(args));
  }

  public isSiteCreatorOrHigher(): boolean {
    const siteSessionRole = this.server.sessionSite() && this.server.sessionSite().role;
    if (siteSessionRole != null) {
      return SiteRolesResource.isSiteCreatorOrHigher(siteSessionRole);
    } else {
      return false;
    }
  }

  public updateColumnTags(params: ApiRequestsTypes.ColumnTagsParams): Promise<void> {
    const Tables = ServiceLocator.get<Tables>('Tables');
    const  { luids, tagsToAdd, tagsToRemove } = params;
    return Tables.changeTagsOnColumns({ columnLuids: luids, tagsToAdd, tagsToRemove })
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.changeTagsColumnsSuccess({
            ITEM_COUNT: luids.length,
            ITEM_COUNT_LOC: Formatters.formatCount(luids.length),
          })
        });
      })
      .catch(e => {
        const errorResponse = e as RestApiErrorResponse;
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.changeTagsColumnsError({
            ITEM_COUNT: luids.length,
            ERROR_MESSAGE: errorResponse.data.error.detail,
          })
        });
        throw e;
      });
  }
}

Module.instance().service('DataCatalogApiRequestsService', DataCatalogApiRequestsService);
